import React, { useEffect, useRef, useState } from 'react';
import { listMedia, uploadMedia, deleteMedia, resolveFileUrl, myStorage } from '../lib/api';
import { UploadCloud, Trash2, Loader2, Copy, Image as ImageIcon, Search, Check } from 'lucide-react';
import { toast } from 'sonner';
import StorageMeter from '../components/StorageMeter';

export default function MediaLibrary({ onPick, selectedUrl }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [storage, setStorage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(0);
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [m, s] = await Promise.all([listMedia(), myStorage()]);
      setItems(m); setStorage(s);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doUpload = async (files) => {
    if (!files?.length) return;
    for (const f of files) {
      if (!f.type.startsWith('image/')) { toast.error(`${f.name}: not an image`); continue; }
      setUploading(0);
      try {
        const doc = await uploadMedia(f, (pct) => setUploading(pct));
        setItems(list => [doc, ...list]);
      } catch { /* quota toast handled by global interceptor */ }
    }
    setUploading(0); load();
  };

  const doDelete = async (m) => {
    if (!window.confirm(`Delete "${m.name}"?`)) return;
    try { await deleteMedia(m.id); setItems(list => list.filter(x => x.id !== m.id)); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(resolveFileUrl(url)); toast.success('URL copied'); }
    catch {}
  };

  const filtered = items.filter(m => (m.name || '').toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="media-library">
      <div className="flex items-center gap-2 text-[#a78bfa]"><ImageIcon className="w-5 h-5" /><span className="uppercase text-[11px] tracking-widest font-bold">Media Library</span></div>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-2">
        <h1 className="font-display text-[32px] md:text-[40px] leading-none">Thumbnails & images</h1>
      </div>
      <p className="text-white/60 text-[14px] mt-1">Private library — only you see your media. Use for video thumbnails, ad creatives, and channel art.</p>

      {storage && <div className="mt-5 max-w-[520px]"><StorageMeter data={storage} /></div>}

      <div className="card-shell p-4 mt-6 flex flex-wrap items-center gap-3">
        <label className="btn-primary cursor-pointer" data-testid="media-upload-btn">
          <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={e => doUpload(Array.from(e.target.files || []))} data-testid="media-file-input" />
          <UploadCloud className="w-4 h-4" /> Upload images
        </label>
        {uploading > 0 && uploading < 100 && (
          <div className="flex items-center gap-2 text-[13px] text-white/70"><Loader2 className="w-4 h-4 animate-spin text-[#22d3ee]" /> {Math.round(uploading)}%</div>
        )}
        <div className="flex-1" />
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name" className="input-field pl-9" data-testid="media-search" />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-white/60 py-12"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="card-shell p-12 text-center mt-6">
          <ImageIcon className="w-10 h-10 mx-auto text-white/30" />
          <div className="text-white/75 font-semibold mt-3">{items.length === 0 ? 'No images yet' : 'Nothing matches that search'}</div>
          <div className="text-[13px] text-white/45 mt-1">{items.length === 0 ? 'Upload PNG, JPG or WEBP up to 10 MB each.' : 'Try a different search term.'}</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 mt-6">
          {filtered.map(m => (
            <MediaCard key={m.id} m={m} onPick={onPick} onDelete={doDelete} onCopy={copy} selected={selectedUrl === m.url} />
          ))}
        </div>
      )}
    </div>
  );
}

function MediaCard({ m, onPick, onDelete, onCopy, selected }) {
  return (
    <div className={`group card-shell overflow-hidden transition ${selected ? 'ring-2 ring-[#22d3ee]' : ''}`} data-testid={`media-card-${m.id}`}>
      <div className="relative aspect-video bg-black">
        <img src={resolveFileUrl(m.url)} alt={m.name} className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition" style={{ background: 'linear-gradient(0deg, rgba(0,0,0,0.85), rgba(0,0,0,0.1))' }} />
        <div className="absolute bottom-2 left-2 right-2 opacity-0 group-hover:opacity-100 transition flex gap-1.5">
          {onPick && (
            <button onClick={() => onPick(m)} className="btn-primary !py-1.5 !px-2.5 text-[12px] flex-1" data-testid={`media-pick-${m.id}`}>
              {selected ? <><Check className="w-3.5 h-3.5" /> Selected</> : <>Use as thumbnail</>}
            </button>
          )}
          <button onClick={() => onCopy(m.url)} className="btn-ghost !py-1.5 !px-2.5" title="Copy URL"><Copy className="w-3.5 h-3.5" /></button>
          <button onClick={() => onDelete(m)} className="btn-ghost !py-1.5 !px-2.5 hover:!text-[#ff6b8a]" title="Delete" data-testid={`media-delete-${m.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      <div className="p-2.5">
        <div className="text-[12.5px] font-semibold truncate">{m.name}</div>
        <div className="text-[11px] text-white/45">{(m.size / 1024).toFixed(0)} KB</div>
      </div>
    </div>
  );
}
